import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ContentWrapper } from '../../Material/create/Styled';
import BackButton from '../../../shared/components/BackButton';
import {
    DetailKpiCategoryTitleBar,
    DetailKpiRevenueTitleBar,
    kpiCreateCategoryFields,
    kpiCreateRevenueFields,
    kpiDetailCategoryFields,
} from '../../../configs/kpi';
import Title from '../create/Title';
import { SingleKpi } from '../single';
import { api } from '../../../shared/hooks/api';
import { error, success } from '../../../utils/toast';
import apiMethods from '../../../utils/api';
import { WarningUnsavedChanges } from '../../../configs/warningProductModal';
import CancelModal from '../../../shared/components/CancelModal';

const DetailKpi = () => {
    const { kpiId } = useParams();
    const [validated, setValidated] = useState(false);
    const navigate = useNavigate();
    const [isModified, setIsModified] = useState(false);
    const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
    const [navigationPath, setNavigationPath] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const [
        { data, isLoading, setLocalData, setLocalUrl: setGetUrl },
        fetchKpi,
    ] = api.get(`/kpi/${kpiId}/`);

    const kpi = data;

    useEffect(() => {
        setGetUrl(`/kpi/${kpiId}/`);
    }, [kpiId, setGetUrl]);

    const markAsModified = useCallback(() => {
        setIsModified(true);
    }, []);

    useEffect(() => {

        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            if (isModified) {
                event.preventDefault();
                event.returnValue = "";
                return "";
            }
        };
        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, [isModified]);

    

    const title =
        kpi?.type === 'Category' ? `Chỉ tiêu ${kpi?.subtype}` : `${kpi?.name}`;

    const createTitleButtonText =
        kpi?.type === 'Category'
            ? DetailKpiCategoryTitleBar
            : DetailKpiRevenueTitleBar;
    const kpiFields =
        kpi?.type === 'Category'
            ? kpiDetailCategoryFields
            : kpiCreateRevenueFields;

    const handleSubmit = async () => {
        if (isSaving) return;
        setIsSaving(true);
        try {
            await apiMethods.patch(`/kpi/${kpiId}/update/`, kpi);
            await fetchKpi();
            success('THÀNH CÔNG', 'Cập nhật chỉ tiêu thành công');
        } catch (err: any) {
            error('Lỗi cập nhật chỉ tiêu', err);
        } finally {
            setIsSaving(false);
        }
    };

    const handleChangeKpiInfo = (updateDate: any) => {
        setLocalData({
            ...kpi,
            ...updateDate,
        });
    };

    const handleSaveAndNavigate = async () => {
        setShowUnsavedDialog(false);
        try {
            await handleSubmit();
            navigate(navigationPath);
        } catch (err) {
            error('Lỗi cập nhật chỉ tiêu', err);
        }
    };

    const handleDontSaveAndNavigate = () => {
        setShowUnsavedDialog(false);
        navigate(navigationPath);
    };

    const handleDataChange = useCallback((updateKpi: any) => {
        markAsModified();
        setLocalData({
            ...kpi,
            ...updateKpi,
        });
    }, [markAsModified, setLocalData, kpi]);

    const confirmNavigation = useCallback((path: string) => {
        if (isModified) {
            setShowUnsavedDialog(true);
            setNavigationPath(path);
            return false;
        }

        navigate(path); 
        return true;
    }, [isModified, navigate]);

    const handleBackClick = () => {
        confirmNavigation("/kpis/");
    };

    const handleContinueEditing = () => {
        setShowUnsavedDialog(false);
    };

    return (
        <ContentWrapper>
            <BackButton label="Trở về" onClick={handleBackClick} />
            <Title
                title={title}
                setValidate={setValidated}
                submitForm={handleSubmit}
                createTitleButtonText={createTitleButtonText}
                onDataChange={markAsModified}
                hasUnsavedChanges={isModified}
                onCancelClick={() => confirmNavigation("/kpis/")}
                isLoading={isSaving}
            />
            <SingleKpi
                kpi={kpi}
                mergeKpi={handleDataChange}
                validated={validated}
                kpiFields={kpiFields}
            />
            <CancelModal
                open={showUnsavedDialog}
                handleConfirm={handleSaveAndNavigate}
                handleClose={handleDontSaveAndNavigate}
                handleCancel={handleContinueEditing}
                title={WarningUnsavedChanges.title}
                contentText={WarningUnsavedChanges.context}
                closeButtonText={WarningUnsavedChanges.closeButtonText}
                confirmButtonText={WarningUnsavedChanges.confirmButtonText}
                cancelButtonText={WarningUnsavedChanges.cancelButtonText}
                showCancelButton={true}
                isLoading={isSaving}
            />
        </ContentWrapper>
    );
};

export default DetailKpi;
